import { User as OystehrUser } from '@oystehr/sdk';
import { Coding, Practitioner } from 'fhir/r4b';
import { PARTICIPATION_CODE_SYSTEM } from '../../fhir/constants';

export type User = OystehrUser & {
  profileResource?: Practitioner;
  faxNumber?: string;
  addressLine1?: string;
  addressLine2?: string;
  addressCity?: string;
  addressState?: string;
  addressZip?: string;
  birthDate?: string;
};

/**
 * Whether an Oystehr user's profile points at a Practitioner.
 *
 * Self-registered users land on a Patient profile instead, which is what "needs review" means in the
 * employee list: they hold no role and have no clinician record yet. `update-user` creates the
 * Practitioner and repoints the profile the first time such a user is saved.
 */
export const hasPractitionerProfile = (profile: string | undefined): boolean =>
  profile?.startsWith('Practitioner/') ?? false;

/**
 * Whether a role name is one of the roles an employee can hold.
 *
 * A project carries roles that aren't employee roles at all — a self-registered user holds
 * `Patient` — and those must not be echoed back when saving an employee record, both because
 * `update-user` rejects them and because converting such a user to staff is precisely the point at
 * which the old role should fall away.
 *
 * Checks the whole {@link RoleType} enum rather than `AVAILABLE_EMPLOYEE_ROLES`, which additionally
 * keeps `Inactive` — a real role, just not one assigned from the checkbox list. Deactivation state
 * is the activation zambda's business, so the form passes it through untouched rather than silently
 * reactivating someone as a side effect of an unrelated edit.
 */
export const isRoleType = (roleName: string): roleName is RoleType =>
  (Object.values(RoleType) as string[]).includes(roleName);

export enum RoleType {
  Administrator = 'Administrator',
  BillingAdmin = 'BillingAdmin',
  // Clinical staff without an NPI (e.g. nurses, medical assistants). Provider-level EHR access
  // except NPI-gated actions (sign notes, e-prescribe, external labs & imaging, claims, in-house meds).
  Clinician = 'Clinician',
  CustomerSupport = 'Customer Support',
  Inactive = 'Inactive',
  Manager = 'Manager',
  Provider = 'Provider',
  Staff = 'Staff',
}

/** Roles allowed to print a patient's clinical documents from the EHR. */
export const CHART_DOCUMENT_ROLES: RoleType[] = [
  RoleType.Administrator,
  RoleType.Manager,
  RoleType.Provider,
  RoleType.Clinician,
  RoleType.Staff,
];

/**
 * Roles allowed into the administration area.
 *
 * Shared so the navigation and the endpoints behind it cannot disagree: a page the UI offers but the
 * server refuses is indistinguishable from a broken feature, and the reverse is a gap.
 */
export const ADMIN_TIER_ROLES: RoleType[] = [RoleType.Administrator, RoleType.Manager, RoleType.CustomerSupport];

export const ALL_EHR_STAFF_ROLES: RoleType[] = [
  RoleType.Administrator,
  RoleType.Manager,
  RoleType.CustomerSupport,
  RoleType.Staff,
  RoleType.Provider,
  RoleType.Clinician,
];

export interface AccessPolicy {
  rule: {
    action: string | string[];
    resource: string | string[];
    effect: 'Allow' | 'Deny';
  }[];
}

export const UserRole = (code: string, display: string): Coding[] => [
  {
    system: PARTICIPATION_CODE_SYSTEM,
    code,
    display,
  },
];

export const AVAILABLE_EMPLOYEE_ROLES: {
  value: RoleType;
  label: string;
  hint: string;
}[] = [
  {
    value: RoleType.Administrator,
    label: 'Administrator',
    hint: `Adjust full settings for entire system`,
  },
  {
    value: RoleType.Manager,
    label: 'Manager',
    hint: `Adjust operating hours or schedule overrides; adjust pre-booked visits per hour`,
  },
  {
    value: RoleType.BillingAdmin,
    label: 'Billing Admin',
    hint: `Administrator access in the Billing App. No access to the EHR.`,
  },
  {
    value: RoleType.Staff,
    label: 'Staff',
    hint: `No settings changes; essentially read-only`,
  },
  {
    value: RoleType.Provider,
    label: 'Provider',
    hint: `A clinician, such as a doctor, a PA or an NP`,
  },
  {
    value: RoleType.Clinician,
    label: 'Clinician',
    hint: `Clinical staff without an NPI, such as a nurse or medical assistant. Provider access except NPI-gated actions.`,
  },
  {
    value: RoleType.CustomerSupport,
    label: 'Customer Support',
    hint: `A customer support representative`,
  },
];
